import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { navigateTo } from '../services/router.js';
import * as companyApi from '../services/company-api.js';
import * as founderBoardApi from '../services/founder-board-api.js';
import type { Company } from '../services/company-api.js';
import type { FounderTicket } from '../services/founder-board-api.js';
import '../components/company/cp-company-card.js';
import '../components/company/cp-company-skyline.js';
import '../components/shared/cp-page-subnav.js';

const COMPANY_TABS = [
  { label: 'Firmen', path: '/companies' },
  { label: 'Gruender Board', path: '/founder-board' },
];

type ViewMode = 'grid' | 'skyline';

@customElement('cp-page-companies')
export class CpPageCompanies extends LitElement {
  createRenderRoot() { return this; }

  @state() private _companies: Company[] = [];
  @state() private _tickets: FounderTicket[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _showCreateForm = false;
  @state() private _creating = false;
  @state() private _newName = '';
  @state() private _newType = '';
  @state() private _newDescription = '';
  @state() private _viewMode: ViewMode = 'grid';

  override connectedCallback(): void {
    super.connectedCallback();
    void this._load();
  }

  private async _load(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const [companies, tickets] = await Promise.all([
        companyApi.listCompanies(),
        founderBoardApi.listTickets().catch(() => [] as FounderTicket[]),
      ]);
      this._companies = companies;
      this._tickets = tickets;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden der Firmen';
    } finally {
      this._loading = false;
    }
  }

  private _openTicketsFor(companyId: number): number {
    return this._tickets.filter(t => t.company_id === companyId && (t.status === 'open' || t.status === 'waiting')).length;
  }

  private _handleCompanySelected(e: CustomEvent<{ companyId: number }>): void {
    navigateTo(`/companies/${e.detail.companyId}`);
  }

  private async _handleCreate(): Promise<void> {
    if (!this._newName.trim()) return;
    this._creating = true;
    this._error = '';
    try {
      const company = await companyApi.createCompany({
        name: this._newName.trim(),
        type: this._newType.trim() || undefined,
        description: this._newDescription.trim() || undefined,
      });
      this._companies = [...this._companies, company];
      this._showCreateForm = false;
      this._newName = '';
      this._newType = '';
      this._newDescription = '';
      navigateTo(`/companies/${company.id}`);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Erstellen der Firma';
    } finally {
      this._creating = false;
    }
  }

  private _renderCreateForm() {
    return html`
      <div class="cp-page-companies__create-form">
        <h3 class="cp-page-companies__create-title">Neue Firma gruenden</h3>

        <div class="cp-page-companies__field">
          <label class="cp-page-companies__label">Name *</label>
          <input
            type="text"
            placeholder="Firmenname"
            .value=${this._newName}
            @input=${(e: Event) => { this._newName = (e.target as HTMLInputElement).value; }}
            class="cp-page-companies__input"
          />
        </div>

        <div class="cp-page-companies__field">
          <label class="cp-page-companies__label">Typ (optional)</label>
          <input
            type="text"
            placeholder="z.B. SaaS, Agentur, E-Commerce"
            .value=${this._newType}
            @input=${(e: Event) => { this._newType = (e.target as HTMLInputElement).value; }}
            class="cp-page-companies__input"
          />
        </div>

        <div class="cp-page-companies__field">
          <label class="cp-page-companies__label">Beschreibung (optional)</label>
          <textarea
            placeholder="Was macht diese Firma?"
            .value=${this._newDescription}
            @input=${(e: Event) => { this._newDescription = (e.target as HTMLTextAreaElement).value; }}
            rows="2"
            class="cp-page-companies__textarea"
          ></textarea>
        </div>

        <div class="cp-page-companies__form-actions">
          <button
            @click=${this._handleCreate}
            ?disabled=${this._creating || !this._newName.trim()}
            class="cp-page-companies__btn-primary"
          >
            ${this._creating ? 'Wird erstellt...' : 'Firma gruenden'}
          </button>
          <button
            @click=${() => { this._showCreateForm = false; this._error = ''; }}
            class="cp-page-companies__btn-secondary"
          >
            Abbrechen
          </button>
        </div>
      </div>
    `;
  }

  private _renderViewToggle() {
    return html`
      <div class="cp-page-companies__view-toggle">
        <button
          class="cp-page-companies__view-btn ${this._viewMode === 'grid' ? 'cp-page-companies__view-btn--active' : ''}"
          @click=${() => { this._viewMode = 'grid'; }}
          title="Grid-Ansicht"
        >&#9638;</button>
        <button
          class="cp-page-companies__view-btn ${this._viewMode === 'skyline' ? 'cp-page-companies__view-btn--active' : ''}"
          @click=${() => { this._viewMode = 'skyline'; }}
          title="3D Skyline"
        >&#9670;</button>
      </div>
    `;
  }

  private _renderHierarchy() {
    const holding = this._companies.find(c => c.is_meta === 1);
    const subsidiaries = this._companies.filter(c => c.is_meta !== 1);

    if (!holding) {
      return this._renderFlatGrid(this._companies);
    }

    return html`
      <div class="cp-hierarchy" @company-selected=${this._handleCompanySelected}>
        <!-- Holding Card -->
        <div class="cp-hierarchy__holding">
          <button
            class="cp-hierarchy__holding-card"
            @click=${() => this._handleCompanySelected(new CustomEvent('company-selected', { detail: { companyId: holding.id } }))}
          >
            <div class="cp-hierarchy__holding-icon"
              style="background-color: ${holding.color ?? '#7c3aed'}"
            >
              ${holding.icon ?? holding.name.split(' ').filter((w: string) => w.length > 0).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div class="cp-hierarchy__holding-info">
              <h2 class="cp-hierarchy__holding-name">${holding.name}</h2>
              ${holding.description ? html`
                <p class="cp-hierarchy__holding-desc">${holding.description}</p>
              ` : nothing}
            </div>
            ${this._openTicketsFor(holding.id) > 0 ? html`
              <span class="cp-company-card__notification cp-company-card__notification--warning">${this._openTicketsFor(holding.id)}</span>
            ` : nothing}
          </button>
        </div>

        <!-- Connector -->
        ${subsidiaries.length > 0 ? html`
          <div class="cp-hierarchy__connector">
            <div class="cp-hierarchy__trunk"></div>
            <div class="cp-hierarchy__node"></div>
            <div class="cp-hierarchy__spread"></div>
          </div>

          <!-- Subsidiary Cards -->
          <div class="cp-hierarchy__subsidiaries">
            ${subsidiaries.map((c) => html`
              <cp-company-card .company=${c} .openTickets=${this._openTicketsFor(c.id)}></cp-company-card>
            `)}
          </div>
        ` : html`
          <p class="cp-hierarchy__empty-subs">Noch keine Firmen in der Holding</p>
        `}
      </div>
    `;
  }

  private _renderFlatGrid(companies: Company[]) {
    return html`
      <div
        class="cp-page-companies__grid"
        @company-selected=${this._handleCompanySelected}
      >
        ${companies.map((c) => html`
          <cp-company-card .company=${c} .openTickets=${this._openTicketsFor(c.id)}></cp-company-card>
        `)}
      </div>
    `;
  }

  private _renderContent() {
    if (this._loading) {
      return html`<div class="cp-page-companies__loading">Firmen werden geladen...</div>`;
    }

    if (this._companies.length === 0) {
      return html`
        <div class="cp-page-companies__empty">
          <p class="cp-page-companies__empty-text">Noch keine Firmen gegruendet.</p>
          ${!this._showCreateForm ? html`
            <button
              @click=${() => { this._showCreateForm = true; }}
              class="cp-page-companies__empty-btn"
            >
              Erste Firma gruenden
            </button>
          ` : nothing}
        </div>
      `;
    }

    if (this._viewMode === 'skyline') {
      return html`
        <cp-company-skyline
          .companies=${this._companies}
          @company-selected=${this._handleCompanySelected}
        ></cp-company-skyline>
      `;
    }

    return this._renderHierarchy();
  }

  override render() {
    return html`
      <div class="cp-page">
        <cp-page-subnav .tabs=${COMPANY_TABS} activePath="/companies"></cp-page-subnav>
        <div class="cp-page-companies__header">
          <div class="cp-page-companies__header-row">
            <h1 class="cp-page__title">Firmen</h1>
            <div style="display:flex;gap:8px;align-items:center">
              ${this._companies.length > 0 ? this._renderViewToggle() : nothing}
              <button
                @click=${() => { this._showCreateForm = !this._showCreateForm; }}
                class="cp-page-companies__add-btn"
              >
                + Neue Firma
              </button>
            </div>
          </div>
        </div>

        <div class="cp-page__content">
          ${this._error ? html`
            <div class="cp-page-companies__error">${this._error}</div>
          ` : nothing}

          ${this._showCreateForm ? this._renderCreateForm() : nothing}

          ${this._renderContent()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-companies': CpPageCompanies;
  }
}
